import { http } from '@/providers/apis/http';
import { taxonomySerializer } from '@/providers/serializers/taxonomy/taxonomy';
import { SubjectModel } from '@/models/taxonomy/subject';
import { ClassificationModel } from '@/models/taxonomy/classification';
import { GradeModel } from '@/models/taxonomy/grade';
import { TxGradeBoundary } from '@/models/taxonomy/grade-boundary';
import { TaxonomyCode } from '@/models/taxonomy/code';
import { GradeCourseModel } from '@/models/content/grade-course';

export class TaxonomyAPI {

  private static INSTANCE = new TaxonomyAPI();

  private namespace = 'api/nucleus/v2/taxonomy';

  private taxonomyNamespace = 'api/nucleus/v1/taxonomy';

  private dsNamespace = 'api/ds/users/v2/tx';

  private namespace1 = 'api/nucleus/v1/taxonomy';

  private heirarchynamespace = 'api/groups/v1/flexible-groups';

  private missionControlNamespaceV2 = 'api/missioncontrol/v2';

  private missionControlNamespace = 'api/missioncontrol/v1';

  private adminNamespace = 'api/admin/v1';

  private adminTaxonomyNamespace = 'api/admin/v1/taxonomy';

  private adminUtilsNamespace = 'api/v1/admin-utils';

  static get instance() {
    return this.INSTANCE;
  }

  public fetchTaxonomyCountry(): Promise<any[]> {
    const endpoint = `${this.heirarchynamespace}/country`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyCountry(response.data);
    });
  }

  public fetchTaxonomyState(country: any): Promise<any[]> {
    const endpoint = `${this.heirarchynamespace}/state?parentId=${country.id}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyState(response.data);
    });
  }

  public fetchTaxonomyDistrict(state: any): Promise<any[]> {
    const endpoint = `${this.heirarchynamespace}/school_district?parentId=${state.id}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyDistrict(response.data);
    });
  }

  public fetchTaxonomyIndiaDistrict(state: any): Promise<any[]> {
    const endpoint = `${this.heirarchynamespace}/district?parentId=${state.id}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyIndiaDistrict(response.data);
    });
  }
  public fetchTaxonomyClassifications(): Promise<ClassificationModel[]> {
    const endpoint = `${this.namespace}/classifications`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyClassifications(response.data);
    });
  }

  public fetchTaxonomyClassificationsList(): Promise<ClassificationModel[]> {
    const endpoint = `${this.adminTaxonomyNamespace}/classifications`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyClassificationsList(response.data);
    })
      .catch((error: any) => {
        // Handle any errors during the HTTP request
        return [];
      });
  }

  public fetchTaxonomySubjects(category: string): Promise<SubjectModel[]> {
    const endpoint = `${this.dsNamespace}/subjects`;
    const headers = http.getTokenHeaders();
    const data = {
      classificationType: category,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeTaxonomySubjects(response.data, category);
    });
  }

  public fetchSubjectsList(category: string): Promise<any> {
    const endpoint = `${this.adminTaxonomyNamespace}/subjects`;
    const headers = http.getTokenHeaders();
    const data = {
      classification_type: category,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeSubjectsList(response.data, category);
    })
      .catch((error: any) => {
        // Handle any errors during the HTTP request
        return [];
      });
  }

  public fetchSubjects(category: string): Promise<SubjectModel[]> {
    const endpoint = `${this.namespace}/subjects`;
    const headers = http.getTokenHeaders();
    const data = {
      classification_type: category,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeTaxonomySubjects(response.data, category);
    });
  }

  public fetchAllFrameworksBySubCode(subCode: any) {
    const endpoint = `${this.adminUtilsNamespace}/frameworks/${subCode}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyFrameWork(response.data);
    });
  }

  public fetchFrameworks(subjectCode: string) {
    const endpoint = `${this.namespace1}/frameworks/subjects`;
    const headers = http.getTokenHeaders();
    const data = {
      subject: subjectCode,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeTaxonomyFrameWork(response.data);
    });
  }

  public fetchSubjectById(subjectCode: string) {
    const endpoint = `${this.namespace1}/subjects/${subjectCode}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return response.data;
    });
  }

  public fetchTaxonomyGrades(subject: string, framework?: any): Promise<GradeModel[]> {
    const endpoint = `${this.dsNamespace}/grades`;
    const headers = http.getTokenHeaders();
    const data = {
      subject,
      framework,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeTaxonomyGrades(response.data);
    });
  }

  public fetchTaxonomyGradesWithFwCode(subject: string, fwCode?: any): Promise<GradeModel[]> {
    const endpoint = `${this.dsNamespace}/grades`;
    const headers = http.getTokenHeaders();
    const data = {
      subject,
      fw_code: fwCode,
    };
    return http.get(endpoint, headers, data).then((response) => {
      return taxonomySerializer.serializeTaxonomyGrades(response.data);
    });
  }

  public fetchTaxonomyGradeBoundaries(gradeId: number | any): Promise<TxGradeBoundary[]> {
    const endpoint = `${this.dsNamespace}/grade/boundary/${gradeId}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyGradeBoundaries(response.data);
    });
  }

  public fetchTaxonomyCodes(
    frameworkId: string,
    subjectId: string,
    courseId: string,
    domainId: string): Promise<TaxonomyCode[]> {
    const endpoint = `${this.taxonomyNamespace}/frameworks/${frameworkId}/subjects/${subjectId}/courses/${courseId}/domains/${domainId}/codes`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyCodes(response.data);

    });
  }

  public fetchCodes(frameworkId: any, subjectId: any, courseId: any, domainId: any): Promise<any> {
    const namespace = this.namespace1;
    const endpoint = `${namespace}/frameworks/${frameworkId}/subjects/${subjectId}/courses/${courseId}/domains/${domainId}/codes`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.normalizefetchCode(response.data);
    });
  }

  public fetchTaxonomyCourse(subject: any): Promise<GradeCourseModel[]> {
    const endpoint = `${this.taxonomyNamespace}/frameworks/${subject.frameworkId || 'GDT'}/subjects/${subject.id}/courses`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return taxonomySerializer.serializeTaxonomyCourse(response.data);
    });
  }

  public fetchSubjectDomainTopicMetadata(params: any) {
    const endpoint = `${this.missionControlNamespaceV2}/tx/subject/domain/topics`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return taxonomySerializer.serializeDomainTopicMetadata(response.data);
    });
  }

  public fetchDomainTopicCompetencyMatrix(params: any) {
    const endpoint = `${this.missionControlNamespace}/tx/competency/matrix/domain/topics`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return taxonomySerializer.serializeDomainTopicMatrix(response.data);
    });
  }

  public fetchClassesBySchool(params: any) {
    const endpoint = `${this.adminNamespace}/classes/school`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return taxonomySerializer.serializeSubjectClasses(response.data, params);
    }).catch((error: any) => {
      return [];
    });
  }
}

export const taxonomyAPI = TaxonomyAPI.instance;
